import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, forkJoin, map, of } from 'rxjs';
import { Customer } from '../../classes/customer.class';
import { Entity } from '../../classes/entity.class';
import { Hiring } from '../../classes/hiring';
import { PlanningHistoryEntry } from '../../classes/planning-history-entry';
import { User } from '../../classes/user.class';
import { CodaltComponent } from '../../codalt.component';
import { CustomerService } from '../../services/customer.service';
import { EntitiesService } from '../../services/entities/entities.service';
import { EntityField, EntityType, FieldType } from '../../services/entities/entity-type.class';
import { HiringService } from '../../services/hiring.service';
import { Person, PersonService } from '../../services/person.service';
import { UserService } from '../../services/user/user.service';

type IdType = 'user' | 'entity' | 'hiring' | 'person' | 'customer';

const FALLBACK_ID_LABELS: Record<string, { label: string; type?: IdType }> = {
    user_id: { label: 'Gebruiker', type: 'user' },
    driver_user_id: { label: 'Chauffeur', type: 'user' },
    performer_id: { label: 'Uitvoerder', type: 'user' },
    mechanic_id: { label: 'Monteur', type: 'user' },
    entity_id: { label: 'Materieel', type: 'entity' },
    truck_entity_id: { label: 'Vrachtwagen', type: 'entity' },
    lowloader_entity_id: { label: 'Dieplader', type: 'entity' },
    hiring_id: { label: 'Inhuur', type: 'hiring' },
    person_id: { label: 'Contactpersoon', type: 'person' },
    afas_customer_id: { label: 'Klant', type: 'customer' },
};

const FIELD_ID_TYPES: Partial<Record<FieldType, IdType>> = {
    [FieldType.UserId]: 'user',
    [FieldType.UserIdWithLeaves]: 'user',
    [FieldType.EntityId]: 'entity',
    [FieldType.PersonId]: 'person',
    [FieldType.CustomerId]: 'customer',
};

const MAIN_FIELDS = ['entity_id', 'user_id'];

interface PlanningHistoryGroup {
    items: PlanningHistoryEntry[];
    expanded: boolean;
}

const CALCULATED_WORK_FIELDS = ['work_begindate', 'work_enddate'];

@Component({
    selector: 'app-planning-history-dialog',
    standalone: false,
    templateUrl: './planning-history-dialog.component.html',
    styleUrl: './planning-history-dialog.component.scss',
})
export class PlanningHistoryDialogComponent extends CodaltComponent implements OnInit {
    users: User[] = [];
    entities: Entity[] = [];
    hirings: Hiring[] = [];
    persons: Person[] = [];
    customers: Customer[] = [];
    readonly groups: PlanningHistoryGroup[];
    readonly dateFormat = 'EEE d MMM y HH:mm';

    private readonly fieldMap: Record<string, EntityField>;

    constructor(
        public dialogRef: MatDialogRef<PlanningHistoryDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: { items: PlanningHistoryEntry[]; entityType: EntityType; fieldLabels?: Record<string, string> },
        private userService: UserService,
        private entitiesService: EntitiesService,
        private hiringService: HiringService,
        private personService: PersonService,
        private customerService: CustomerService
    ) {
        super();
        this.fieldMap = {
            ...(this.data.entityType?.fields_planning ?? {}),
            ...(this.data.entityType?.fields_planning_data ?? {}),
            ...(this.data.entityType?.fields_phe ?? {}),
            ...(this.data.entityType?.fields_phe_data ?? {}),
        };
        this.groups = this.buildGroups(this.data.items ?? []);
    }

    // Eén groep per opslaan van één bron-regel door één gebruiker; elk veld blijft een eigen rij.
    private buildGroups(items: PlanningHistoryEntry[]): PlanningHistoryGroup[] {
        const groups = new Map<string, PlanningHistoryGroup>();

        for (const item of items) {
            const key = [item.source, item.source_row_id, item.changed_by_id, item.created_at].join('|');
            if (!groups.has(key)) {
                groups.set(key, { items: [], expanded: false });
            }
            groups.get(key).items.push(item);
        }

        for (const group of groups.values()) {
            group.items.sort((a, b) => +MAIN_FIELDS.includes(b.field) - +MAIN_FIELDS.includes(a.field));
        }

        return [...groups.values()];
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.userService.getList(true).subscribe(users => {
                this.users = users;
            })
        );

        this.subscriptions.add(
            this.entitiesService.getList().subscribe(entities => {
                this.entities = entities;
            })
        );

        this.subscriptions.add(
            this.hiringService.getList().subscribe(hirings => {
                this.hirings = hirings;
            })
        );

        // Personen en klanten worden niet gecached en zijn te talrijk om volledig op te halen:
        // alleen de waarden die in deze historie voorkomen.
        const personIds = this.valuesOfType('person').map(Number);
        const customerIds = this.valuesOfType('customer');
        const persons$ = personIds.length
            ? forkJoin(personIds.map(id => this.personService.get(id).pipe(catchError(() => of(null)))))
            : of([]);
        const customers$ = customerIds.length
            ? forkJoin(
                  customerIds.map(id =>
                      this.customerService.search(id, 500, { include_customers_without_area: true }).pipe(
                          map(customers => customers.find(customer => customer.afas_customer_id === id)),
                          catchError(() => of(null))
                      )
                  )
              )
            : of([]);

        this.subscriptions.add(
            forkJoin([persons$, customers$]).subscribe(([persons, customers]) => {
                this.persons = persons.filter(Boolean);
                this.customers = customers.filter(Boolean);
            })
        );
    }

    private valuesOfType(type: IdType): string[] {
        const values = (this.data.items ?? [])
            .filter(item => this.idType(item.field) === type)
            .flatMap(item => [item.old_value, item.new_value])
            .filter(Boolean);

        return [...new Set(values)];
    }

    getLabel(item: PlanningHistoryEntry): string {
        const field = item.field;
        if (field === 'deleted_at') {
            // Verwijderde regel: benoem wát er weg is; de bron (of dit veld) wordt doorgestreept.
            return item.source === 'user_planning'
                ? 'Personeel'
                : (this.environment.entitytypes[item.entitytype_key]?.name ?? 'Materieel');
        }
        if (this.data.fieldLabels && field === 'entity_id') {
            // planning_has_entity bevat ook inhuur en ploegen, dus label per entitytype i.p.v. altijd "Materieel".
            const entityTypeName = this.environment.entitytypes[item.entitytype_key]?.name;
            if (entityTypeName) {
                return entityTypeName;
            }
        }

        return (
            this.data.fieldLabels?.[field] ?? this.fieldMap[field]?.name ?? FALLBACK_ID_LABELS[field]?.label ?? field
        );
    }

    isDateField(item: PlanningHistoryEntry): boolean {
        return item.date_old_value != null || item.date_new_value != null;
    }

    isCheckboxField(field: string): boolean {
        return this.fieldMap[field]?.type === FieldType.Checkbox;
    }

    isTextareaField(field: string): boolean {
        return this.fieldMap[field]?.type === FieldType.Textarea;
    }

    idType(field: string): IdType | null {
        return FIELD_ID_TYPES[this.fieldMap[field]?.type] ?? FALLBACK_ID_LABELS[field]?.type ?? null;
    }
}
